import React, { useState, useEffect } from 'react';
import { Save, User, Briefcase, BookOpen, Mail, Phone, AlertCircle } from 'lucide-react';
import { profilesAPI, authAPI } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { LoadingPage } from '../components/shared';
import toast from 'react-hot-toast';

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    profilesAPI.getMy()
      .then(r => {
        setProfile(r.data.profile);
        setForm(r.data.profile || {});
      })
      .catch(() => toast.error('Failed to load profile'))
      .finally(() => setLoading(false));
  }, []);

  const set = (field) => (e) => {
    const val = e.target.type === 'number' ? parseFloat(e.target.value) : e.target.value;
    setForm(f => ({ ...f, [field]: val }));
  };

  const setArray = (field) => (e) => {
    const arr = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
    setForm(f => ({ ...f, [field]: arr }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await profilesAPI.updateMy(form);
      toast.success('Profile updated');
    } catch {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingPage />;

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">My Profile</h1>
          <p className="page-subtitle">Manage your information and preferences</p>
        </div>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          <Save size={16} /> {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>

      {user?.status === 'pending' && (
        <div style={{
          display: 'flex', gap: 12, padding: '14px 18px',
          background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)',
          borderRadius: 10, marginBottom: 24, alignItems: 'center'
        }}>
          <AlertCircle size={18} style={{ color: 'var(--warning)', flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 600, color: 'var(--warning)', marginBottom: 2 }}>Account Pending Approval</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              Your account is under review by the program admin. You'll receive an email once approved.
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Basic Info */}
        <div className="card" style={{ padding: 28, gridColumn: '1 / -1' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
            <User size={18} style={{ color: 'var(--teal)' }} /> Basic Information
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">First Name</label>
              <input className="input" value={user?.first_name || ''} disabled style={{ opacity: 0.6 }} />
              <span className="form-hint">Contact admin to change your name</span>
            </div>
            <div className="form-group">
              <label className="form-label">Last Name</label>
              <input className="input" value={user?.last_name || ''} disabled style={{ opacity: 0.6 }} />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="input" value={user?.email || ''} disabled style={{ opacity: 0.6 }} />
            </div>
            <div className="form-group">
              <label className="form-label">Role</label>
              <input className="input" value={user?.role || ''} disabled style={{ opacity: 0.6, textTransform: 'capitalize' }} />
            </div>
          </div>
        </div>

        {/* Role-specific fields */}
        {user?.role === 'student' && <StudentFields form={form} set={set} setArray={setArray} />}
        {user?.role === 'lecturer' && <LecturerFields form={form} set={set} setArray={setArray} />}
        {user?.role === 'alumni' && <AlumniFields form={form} set={set} setArray={setArray} />}
      </div>
    </div>
  );
}

function StudentFields({ form, set, setArray }) {
  return (
    <>
      <div className="card" style={{ padding: 28 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
          <BookOpen size={16} style={{ color: 'var(--teal)' }} /> Academic Details
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="form-group">
            <label className="form-label">Matric Number</label>
            <input className="input" value={form.matric_number || ''} onChange={set('matric_number')} placeholder="e.g. STU/2020/001" />
          </div>
          <div className="form-group">
            <label className="form-label">Department</label>
            <input className="input" value={form.department || ''} onChange={set('department')} placeholder="e.g. Computer Science, Design, Business" />
          </div>
          <div className="form-group">
            <label className="form-label">Level</label>
            <select className="input" value={form.level || ''} onChange={set('level')}>
              <option value="">Select level</option>
              {['100L','200L','300L','400L','500L','PG'].map(l => <option key={l}>{l}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">CGPA</label>
            <input className="input" type="number" step="0.01" min="0" max="5" value={form.gpa || ''} onChange={set('gpa')} placeholder="e.g. 4.50" />
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 28 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
          <User size={16} style={{ color: 'var(--teal)' }} /> Skills & Profile
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="form-group">
            <label className="form-label">Skills</label>
            <input className="input" value={Array.isArray(form.skills) ? form.skills.join(', ') : (form.skills || '')} onChange={setArray('skills')} placeholder="Python, MATLAB, Circuit Design..." />
            <span className="form-hint">Comma-separated</span>
          </div>
          <div className="form-group">
            <label className="form-label">Research Interests</label>
            <input className="input" value={Array.isArray(form.interests) ? form.interests.join(', ') : (form.interests || '')} onChange={setArray('interests')} placeholder="AI, Power Systems, Robotics..." />
          </div>
          <div className="form-group">
            <label className="form-label">Portfolio URL</label>
            <input className="input" type="url" value={form.portfolio_url || ''} onChange={set('portfolio_url')} placeholder="https://..." />
          </div>
          <div className="form-group">
            <label className="form-label">Bio</label>
            <textarea className="input" rows={4} value={form.bio || ''} onChange={set('bio')} placeholder="Tell sponsors and lecturers about yourself..." />
          </div>
        </div>
      </div>
    </>
  );
}

function LecturerFields({ form, set, setArray }) {
  return (
    <>
      <div className="card" style={{ padding: 28 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
          <BookOpen size={16} style={{ color: 'var(--teal)' }} /> Academic Profile
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="form-group">
            <label className="form-label">Title</label>
            <select className="input" value={form.title || ''} onChange={set('title')}>
              <option value="">Select title</option>
              {['Dr.', 'Prof.', 'Engr.', 'Mr.', 'Mrs.', 'Ms.'].map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Department</label>
            <input className="input" value={form.department || ''} onChange={set('department')} />
          </div>
          <div className="form-group">
            <label className="form-label">Faculty</label>
            <input className="input" value={form.faculty || ''} onChange={set('faculty')} />
          </div>
          <div className="form-group">
            <label className="form-label">Staff ID</label>
            <input className="input" value={form.staff_id || ''} onChange={set('staff_id')} />
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 28 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Briefcase size={16} style={{ color: 'var(--teal)' }} /> Research & Links
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="form-group">
            <label className="form-label">Research Areas</label>
            <input className="input" value={Array.isArray(form.research_areas) ? form.research_areas.join(', ') : (form.research_areas || '')} onChange={setArray('research_areas')} placeholder="AI, Power Electronics, Signal Processing..." />
            <span className="form-hint">Comma-separated</span>
          </div>
          <div className="form-group">
            <label className="form-label">Expertise</label>
            <input className="input" value={Array.isArray(form.expertise) ? form.expertise.join(', ') : (form.expertise || '')} onChange={setArray('expertise')} />
          </div>
          <div className="form-group">
            <label className="form-label">Google Scholar URL</label>
            <input className="input" type="url" value={form.google_scholar_url || ''} onChange={set('google_scholar_url')} />
          </div>
          <div className="form-group">
            <label className="form-label">Bio</label>
            <textarea className="input" rows={4} value={form.bio || ''} onChange={set('bio')} placeholder="Your research background and interests..." />
          </div>
        </div>
      </div>
    </>
  );
}

function AlumniFields({ form, set, setArray }) {
  return (
    <>
      <div className="card" style={{ padding: 28 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Briefcase size={16} style={{ color: 'var(--teal)' }} /> Professional Profile
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="form-group">
            <label className="form-label">Company / Organisation</label>
            <input className="input" value={form.company || ''} onChange={set('company')} placeholder="e.g. Acme Corp, Google, McKinsey..." />
          </div>
          <div className="form-group">
            <label className="form-label">Role / Title</label>
            <input className="input" value={form.role || ''} onChange={set('role')} placeholder="e.g. Senior Engineer, Product Manager" />
          </div>
          <div className="form-group">
            <label className="form-label">Industry</label>
            <input className="input" value={form.industry || ''} onChange={set('industry')} placeholder="e.g. Oil & Gas, Telecommunications..." />
          </div>
          <div className="form-group">
            <label className="form-label">Years of Experience</label>
            <input className="input" type="number" min="0" value={form.years_experience || ''} onChange={set('years_experience')} />
          </div>
          <div className="form-group">
            <label className="form-label">Graduation Year</label>
            <input className="input" type="number" min="1960" max={new Date().getFullYear()} value={form.graduation_year || ''} onChange={set('graduation_year')} />
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 28 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
          <BookOpen size={16} style={{ color: 'var(--teal)' }} /> Interests & Links
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="form-group">
            <label className="form-label">Research Interests</label>
            <input className="input" value={Array.isArray(form.research_interests) ? form.research_interests.join(', ') : (form.research_interests || '')} onChange={setArray('research_interests')} placeholder="Renewable Energy, IoT, AI..." />
            <span className="form-hint">Comma-separated — used to match you with relevant topics</span>
          </div>
          <div className="form-group">
            <label className="form-label">Expertise</label>
            <input className="input" value={Array.isArray(form.expertise) ? form.expertise.join(', ') : (form.expertise || '')} onChange={setArray('expertise')} />
          </div>
          <div className="form-group">
            <label className="form-label">LinkedIn URL</label>
            <input className="input" type="url" value={form.linkedin_url || ''} onChange={set('linkedin_url')} placeholder="https://linkedin.com/in/..." />
          </div>
          <div className="form-group">
            <label className="form-label">Bio</label>
            <textarea className="input" rows={4} value={form.bio || ''} onChange={set('bio')} placeholder="Share your story and what motivates you to support student research..." />
          </div>
        </div>
      </div>
    </>
  );
}
